import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthenticated, useNotify } from "react-admin";
import axiosWithAuth from "../../utils/axiosWithAuth";
import {
  makeStyles,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Box,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
} from "@material-ui/core";
import Alert from "@mui/material/Alert";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";
import RestoreIcon from "@material-ui/icons/Restore";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import SettingsEthernetIcon from "@material-ui/icons/SettingsEthernet";

const API_BASE = "/admin/predictor-import";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  paper: { padding: theme.spacing(3) },
  sidebarPaper: { padding: 0, overflow: "hidden" },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  stickyActions: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
  },
  sectionTitle: { marginBottom: theme.spacing(2) },
  chipRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  helperText: { marginTop: theme.spacing(1) },
  listHeader: {
    padding: theme.spacing(2, 2, 1, 2),
  },
  listScroll: {
    maxHeight: 640,
    overflowY: "auto",
  },
  selectedListItem: {
    backgroundColor: `${theme.palette.action.selected} !important`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  variantPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  inlineFieldRow: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 2fr) repeat(2, minmax(120px, 1fr))",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  activeYearField: { maxWidth: 280 },
  searchField: { marginTop: theme.spacing(1) },
  columnKey: { fontFamily: "monospace", fontSize: 12 },
  orderButtons: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  emptyState: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
}));

async function apiGet(path) {
  const res = await axiosWithAuth.get(`${API_BASE}${path}`);
  return res.data;
}

async function apiPut(path, data) {
  const res = await axiosWithAuth.put(`${API_BASE}${path}`, data);
  return res.data;
}

async function apiDelete(path) {
  const res = await axiosWithAuth.delete(`${API_BASE}${path}`);
  return res.data;
}

function normalizeWidth(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed <= 0) {
    return fallback;
  }
  return parsed;
}

function normalizeLabel(value, fallback) {
  const label = String(value || "").trim();
  return label || fallback;
}

function renumberColumns(columns) {
  const enabled = columns
    .filter((column) => column.enabled)
    .sort((left, right) => left.order - right.order)
    .map((column, index) => ({ ...column, order: index }));
  const disabled = columns
    .filter((column) => !column.enabled)
    .sort((left, right) => left.key.localeCompare(right.key))
    .map((column, index) => ({
      ...column,
      order: enabled.length + index,
    }));

  return [...enabled, ...disabled];
}

function buildColumnDraft(selectedColumns, availableColumns) {
  const selectedMap = new Map(
    (selectedColumns || []).map((column, index) => [
      column.key,
      { ...column, order: index },
    ]),
  );

  return renumberColumns(
    availableColumns.map((column, index) => {
      const current = selectedMap.get(column.key);
      return {
        key: column.key,
        label: current?.label || column.defaultLabel,
        width: current?.width || column.defaultWidth,
        enabled: Boolean(current),
        order: current ? current.order : (selectedColumns?.length || 0) + index,
        defaultLabel: column.defaultLabel,
        defaultWidth: column.defaultWidth,
      };
    }),
  );
}

function serializeColumns(columns) {
  return renumberColumns(columns)
    .filter((column) => column.enabled)
    .map((column) => ({
      key: column.key,
      label: normalizeLabel(column.label, column.defaultLabel),
      width: normalizeWidth(column.width, column.defaultWidth),
    }));
}

function buildDraftFromState(state) {
  if (!state) {
    return null;
  }

  if (state.tableType === "wide") {
    return {
      kind: "predictor",
      activeYearColumn: state.config.activeYearColumn,
      columns: buildColumnDraft(state.config.columns, state.availableColumns),
    };
  }

  const variantDraft = {};
  state.variantNames.forEach((variantName) => {
    const variantConfig = state.config.variants[variantName];
    variantDraft[variantName] = {
      title: variantConfig.title,
      headerHeight: variantConfig.headerHeight,
      rowHeight: variantConfig.rowHeight,
      firstColumn: { ...variantConfig.firstColumn },
      columns: buildColumnDraft(variantConfig.columns, state.availableColumns),
    };
  });

  return {
    kind: "cutoff",
    variants: variantDraft,
  };
}

function toggleColumn(columns, key, enabled) {
  let nextOrder = columns.filter((column) => column.enabled).length;
  return renumberColumns(
    columns.map((column) => {
      if (column.key !== key) {
        return column;
      }

      return {
        ...column,
        enabled,
        order: enabled ? nextOrder : column.order,
      };
    }),
  );
}

function moveEnabledColumn(columns, key, direction) {
  const orderedColumns = renumberColumns(columns);
  const enabledColumns = orderedColumns.filter((column) => column.enabled);
  const disabledColumns = orderedColumns.filter((column) => !column.enabled);
  const currentIndex = enabledColumns.findIndex((column) => column.key === key);
  const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

  if (
    currentIndex === -1 ||
    targetIndex < 0 ||
    targetIndex >= enabledColumns.length
  ) {
    return orderedColumns;
  }

  const nextEnabled = [...enabledColumns];
  const currentItem = nextEnabled[currentIndex];
  nextEnabled[currentIndex] = nextEnabled[targetIndex];
  nextEnabled[targetIndex] = currentItem;

  return renumberColumns([
    ...nextEnabled.map((column, index) => ({ ...column, order: index })),
    ...disabledColumns,
  ]);
}

function PredictorColumnTable({ columns, onChange }) {
  const displayColumns = renumberColumns(columns);
  const enabledColumns = displayColumns.filter((column) => column.enabled);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Use</TableCell>
            <TableCell>Key</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Width</TableCell>
            <TableCell>Order</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayColumns.map((column) => {
            const enabledIndex = enabledColumns.findIndex(
              (enabledColumn) => enabledColumn.key === column.key,
            );
            const isFirstEnabled = enabledIndex === 0;
            const isLastEnabled = enabledIndex === enabledColumns.length - 1;

            return (
              <TableRow key={column.key}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={column.enabled}
                    color="primary"
                    onChange={(event) =>
                      onChange(
                        toggleColumn(columns, column.key, event.target.checked),
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {column.key}
                  </div>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={column.label}
                    onChange={(event) =>
                      onChange(
                        columns.map((item) =>
                          item.key === column.key
                            ? { ...item, label: event.target.value }
                            : item,
                        ),
                      )
                    }
                    disabled={!column.enabled}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="number"
                    value={column.width}
                    onChange={(event) =>
                      onChange(
                        columns.map((item) =>
                          item.key === column.key
                            ? { ...item, width: event.target.value }
                            : item,
                        ),
                      )
                    }
                    disabled={!column.enabled}
                    inputProps={{ min: 40 }}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onChange(moveEnabledColumn(columns, column.key, "up"))
                      }
                      disabled={!column.enabled || isFirstEnabled}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onChange(moveEnabledColumn(columns, column.key, "down"))
                      }
                      disabled={!column.enabled || isLastEnabled}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const PredictorDisplayConfig = () => {
  useAuthenticated();
  const classes = useStyles();
  const notify = useNotify();

  const [registry, setRegistry] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [configState, setConfigState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [pageError, setPageError] = useState("");

  const filteredRegistry = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) {
      return registry;
    }

    return registry.filter((item) => {
      return (
        item.label.toLowerCase().includes(term) ||
        item.key.toLowerCase().includes(term)
      );
    });
  }, [registry, searchText]);

  const loadRegistry = useCallback(async () => {
    setLoadingList(true);
    setPageError("");
    try {
      const response = await apiGet("/predictors");
      const list = (response.data || []).slice().sort((left, right) => {
        if (left.tableType !== right.tableType) {
          return left.tableType.localeCompare(right.tableType);
        }
        return left.label.localeCompare(right.label);
      });
      setRegistry(list);
      setSelectedKey((currentKey) => currentKey || list[0]?.key || "");
    } catch (error) {
      const message =
        error.response?.data?.error || "Failed to load config list";
      setPageError(message);
      notify(message, "error");
    } finally {
      setLoadingList(false);
    }
  }, [notify]);

  const loadConfig = useCallback(
    async (key) => {
      if (!key) {
        return;
      }

      setLoadingConfig(true);
      setPageError("");
      try {
        const response = await apiGet(`/display-config/${key}`);
        const state = response.data;
        setConfigState(state);
        setDraft(buildDraftFromState(state));
      } catch (error) {
        const message =
          error.response?.data?.error || "Failed to load display config";
        setConfigState(null);
        setDraft(null);
        setPageError(message);
        notify(message, "error");
      } finally {
        setLoadingConfig(false);
      }
    },
    [notify],
  );

  useEffect(() => {
    loadRegistry();
  }, [loadRegistry]);

  useEffect(() => {
    if (selectedKey) {
      loadConfig(selectedKey);
    }
  }, [loadConfig, selectedKey]);

  const handleSave = async () => {
    if (!configState || !draft) {
      return;
    }

    let payload;
    if (configState.tableType === "wide") {
      const columns = serializeColumns(draft.columns);
      if (!columns.some((column) => column.key === draft.activeYearColumn)) {
        notify("Active year column must remain enabled", "warning");
        return;
      }

      payload = {
        activeYearColumn: draft.activeYearColumn,
        columns,
      };
    } else {
      payload = {
        variants: configState.variantNames.reduce((variants, variantName) => {
          const variantDraft = draft.variants[variantName];
          const defaultVariant =
            configState.defaultConfig.variants[variantName];
          variants[variantName] = {
            title: normalizeLabel(variantDraft.title, defaultVariant.title),
            headerHeight: normalizeWidth(
              variantDraft.headerHeight,
              defaultVariant.headerHeight,
            ),
            rowHeight: normalizeWidth(
              variantDraft.rowHeight,
              defaultVariant.rowHeight,
            ),
            firstColumn: {
              label: normalizeLabel(
                variantDraft.firstColumn.label,
                defaultVariant.firstColumn.label,
              ),
              width: normalizeWidth(
                variantDraft.firstColumn.width,
                defaultVariant.firstColumn.width,
              ),
            },
            columns: serializeColumns(variantDraft.columns),
          };
          return variants;
        }, {}),
      };
    }

    setSaving(true);
    try {
      const response = await apiPut(`/display-config/${selectedKey}`, payload);
      const state = response.data;
      setConfigState(state);
      setDraft(buildDraftFromState(state));
      notify("Display config saved", "success");
    } catch (error) {
      notify(
        error.response?.data?.error || "Failed to save display config",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedKey) {
      return;
    }

    const shouldReset = window.confirm(
      "Reset this display config to the built-in defaults?",
    );
    if (!shouldReset) {
      return;
    }

    setResetting(true);
    try {
      const response = await apiDelete(`/display-config/${selectedKey}`);
      const state = response.data;
      setConfigState(state);
      setDraft(buildDraftFromState(state));
      notify("Display config reset to default", "success");
    } catch (error) {
      notify(
        error.response?.data?.error || "Failed to reset display config",
        "error",
      );
    } finally {
      setResetting(false);
    }
  };

  const renderPredictorEditor = () => {
    if (!configState || !draft || configState.tableType !== "wide") {
      return null;
    }

    return (
      <>
        <Typography variant="h6" className={classes.sectionTitle}>
          Predictor Table Settings
        </Typography>
        <TextField
          select
          label="Active year column"
          value={draft.activeYearColumn}
          onChange={(event) => {
            const nextKey = event.target.value;
            setDraft((prev) => ({
              ...prev,
              activeYearColumn: nextKey,
              columns: toggleColumn(prev.columns, nextKey, true),
            }));
          }}
          variant="outlined"
          size="small"
          className={classes.activeYearField}
        >
          {configState.yearColumns.map((columnKey) => (
            <MenuItem key={columnKey} value={columnKey}>
              {columnKey}
            </MenuItem>
          ))}
        </TextField>
        <Box className={classes.chipRow}>
          {configState.yearColumns.map((columnKey) => (
            <Chip key={columnKey} size="small" label={columnKey} />
          ))}
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.helperText}
        >
          Enable the columns you want to show, update their labels and widths,
          and keep the current active year mapped to the live comparison column.
        </Typography>
        <Box mt={2}>
          <PredictorColumnTable
            columns={draft.columns}
            onChange={(columns) => setDraft((prev) => ({ ...prev, columns }))}
          />
        </Box>
      </>
    );
  };

  const renderCutoffEditor = () => {
    if (!configState || !draft || configState.tableType === "wide") {
      return null;
    }

    return (
      <>
        <Typography variant="h6" className={classes.sectionTitle}>
          Cutoff Table Settings
        </Typography>
        {configState.variantNames.map((variantName) => {
          const variantDraft = draft.variants[variantName];
          return (
            <Paper
              key={variantName}
              className={classes.variantPaper}
              elevation={0}
            >
              <Typography variant="subtitle1">
                {variantName === "default"
                  ? "Default variant"
                  : `${variantName} variant`}
              </Typography>
              <Box mt={2} className={classes.inlineFieldRow}>
                <TextField
                  label="Table title"
                  variant="outlined"
                  size="small"
                  value={variantDraft.title}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      variants: {
                        ...prev.variants,
                        [variantName]: {
                          ...prev.variants[variantName],
                          title: event.target.value,
                        },
                      },
                    }))
                  }
                />
                <TextField
                  label="Header height"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={variantDraft.headerHeight}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      variants: {
                        ...prev.variants,
                        [variantName]: {
                          ...prev.variants[variantName],
                          headerHeight: event.target.value,
                        },
                      },
                    }))
                  }
                />
                <TextField
                  label="Row height"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={variantDraft.rowHeight}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      variants: {
                        ...prev.variants,
                        [variantName]: {
                          ...prev.variants[variantName],
                          rowHeight: event.target.value,
                        },
                      },
                    }))
                  }
                />
              </Box>
              <Box className={classes.inlineFieldRow}>
                <TextField
                  label="First column label"
                  variant="outlined"
                  size="small"
                  value={variantDraft.firstColumn.label}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      variants: {
                        ...prev.variants,
                        [variantName]: {
                          ...prev.variants[variantName],
                          firstColumn: {
                            ...prev.variants[variantName].firstColumn,
                            label: event.target.value,
                          },
                        },
                      },
                    }))
                  }
                />
                <TextField
                  label="First column width"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={variantDraft.firstColumn.width}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      variants: {
                        ...prev.variants,
                        [variantName]: {
                          ...prev.variants[variantName],
                          firstColumn: {
                            ...prev.variants[variantName].firstColumn,
                            width: event.target.value,
                          },
                        },
                      },
                    }))
                  }
                />
              </Box>
              <PredictorColumnTable
                columns={variantDraft.columns}
                onChange={(columns) =>
                  setDraft((prev) => ({
                    ...prev,
                    variants: {
                      ...prev.variants,
                      [variantName]: {
                        ...prev.variants[variantName],
                        columns,
                      },
                    },
                  }))
                }
              />
            </Paper>
          );
        })}
      </>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerRow}>
        <div>
          <Typography variant="h5">Predictor Display Config</Typography>
          <Typography variant="body2" color="textSecondary">
            Manage runtime labels, widths, active year columns, and cutoff
            titles without changing backend code.
          </Typography>
        </div>
        <div className={classes.stickyActions}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={loadRegistry}
            disabled={loadingList}
          >
            Refresh List
          </Button>
          <Button
            startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!draft || saving || loadingConfig}
          >
            Save Override
          </Button>
          <Button
            startIcon={
              resetting ? <CircularProgress size={14} /> : <RestoreIcon />
            }
            variant="outlined"
            onClick={handleReset}
            disabled={!selectedKey || resetting || loadingConfig}
          >
            Reset
          </Button>
        </div>
      </div>

      {pageError ? <Alert severity="error">{pageError}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.sidebarPaper} variant="outlined">
            <div className={classes.listHeader}>
              <Typography variant="h6">Configs</Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                className={classes.searchField}
              />
            </div>
            <Divider />
            {loadingList ? (
              <Box className={classes.emptyState}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <List dense className={classes.listScroll}>
                {filteredRegistry.map((item) => (
                  <ListItem
                    button
                    key={item.key}
                    selected={selectedKey === item.key}
                    onClick={() => setSelectedKey(item.key)}
                    className={
                      selectedKey === item.key
                        ? classes.selectedListItem
                        : undefined
                    }
                  >
                    <ListItemText
                      primary={item.label}
                      secondary={`${item.key} • ${
                        item.tableType === "wide" ? "Predictor" : "Cutoff"
                      }`}
                    />
                  </ListItem>
                ))}
                {!filteredRegistry.length ? (
                  <Box className={classes.emptyState}>
                    <Typography variant="body2" color="textSecondary">
                      No matching configs found.
                    </Typography>
                  </Box>
                ) : null}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper className={classes.paper} variant="outlined">
            {loadingConfig ? (
              <Box className={classes.emptyState}>
                <CircularProgress size={28} />
              </Box>
            ) : !configState || !draft ? (
              <Box className={classes.emptyState}>
                <SettingsEthernetIcon
                  color="disabled"
                  style={{ fontSize: 48 }}
                />
                <Typography variant="body2" color="textSecondary">
                  Select a predictor or cutoff config to start editing.
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <div>
                    <Typography variant="h6">{configState.label}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {configState.key} •{" "}
                      {configState.tableType === "wide"
                        ? "Predictor"
                        : "Cutoff"}
                    </Typography>
                  </div>
                  <Chip
                    color={configState.hasOverride ? "primary" : "default"}
                    label={
                      configState.hasOverride
                        ? "Override active"
                        : "Using defaults"
                    }
                    size="small"
                  />
                </Box>

                {renderPredictorEditor()}
                {renderCutoffEditor()}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default PredictorDisplayConfig;
